/**
 * Log actions and middleware for the store. Firing the actions will result in logs!
 */
import * as Sentry from '@sentry/react';
import type { Middleware } from 'redux';
import { Logger, LogLevel } from '@dora/lumberjack';
import { RootState } from '../../store';
import * as LogActions from '../actions/log';

const logger = new Logger('Fixtuur', LogLevel.Info);

export function getLogger(): Logger {
    return logger;
}

export function setLogLevel(level: LogLevel) {
    logger.setOutputLogLevel(level);
}

export const middleware: Middleware = x => next => action => {
    const {
        core: { tenant },
    }: RootState = x.getState();

    if (tenant !== undefined) {
        Sentry.setTag('fixtuur_tenant', tenant);
    }

    if (action.type === LogActions.logActionType) {
        const logLevel = action.payload.level;
        const message = action.payload.message;
        logger.log(logLevel, message);
        if (logLevel === 1) {
            // the equivalent of lumberjack's 'critical' level in Sentry is 'fatal'
            Sentry.captureMessage(message, { level: 'fatal' });
        }
        if (logLevel === 3) {
            Sentry.captureMessage(message, { level: 'warning' });
        }
    }
    if (action.type === 'core/error') {
        // Error Boundary will call 'core/error' when triggered manually, potentially duplicating the error we just caught.
        // This specific 'core/error' has 'type: log' added to it. This tells us the Error Boundary has acknowledged the Error from Core and will render Fallback UI, instead of reporting the same error back as Unhandled.
        const message = action.payload.message;
        logger.error(message);
        Sentry.captureMessage(message, {
            level: 'error',
            tags: {
                type: action.payload.context.type,
                location: action.payload.context.location,
                ...action.payload.context.additionalTags,
            },
        });
    }
    return next(action);
};
